import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Search } from 'lucide-react'
import { api, PROTECTION_DEPOSIT_STATES, type ProtectionDeposit } from '@/api/client'
import { useProperties } from '@/hooks/useApi'
import { pickableProperties } from '@/lib/property-lifecycle'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatLocaleDateTime, getChannelLabel, getPropertyDisplayName } from '@/lib/format-utils'
import {
  describeDeposit,
  disputeLabel,
  formatDepositAmount,
  formatStay,
  historyDetail,
  historyLabel,
  stateHint,
  statePresentation,
} from './deposit-presentation'

/** "unknown" is where Booking.com and newer channels land; say so, not "unknown". */
function channelName(channel: string | null): string {
  return channel === 'unknown' ? 'Other channel' : getChannelLabel(channel)
}

const selectClass =
  'h-9 rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2'

type Window = 'upcoming' | 'past'

/**
 * Every booking except Airbnb, with its deposit, modeled on Uplisting's
 * Deposits tab: who, where, a status, the deposit in plain words, and the stay.
 * A row opens the full hold timeline, which Uplisting's page does not show.
 */
export function DepositsTab({
  initialWindow,
  initialBookingId,
}: {
  /** From `?window=`, so a link can open the right list. */
  initialWindow?: Window
  /**
   * From `?booking=`: open this booking's drawer once the list loads. Deposit
   * alert emails link here (CAP-655), so one click lands on the booking.
   */
  initialBookingId?: string
} = {}) {
  const [stayWindow, setStayWindow] = useState<Window>(initialWindow ?? 'upcoming')
  const [pendingBooking, setPendingBooking] = useState<string | null>(initialBookingId ?? null)
  const [propertyId, setPropertyId] = useState('')
  const [state, setState] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ProtectionDeposit | null>(null)

  const { data: propertiesData } = useProperties()
  const properties = useMemo(
    () =>
      // A property still onboarding has no deposits to filter by (CAP-1467).
      pickableProperties(propertiesData?.properties ?? []),
    [propertiesData]
  )

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['protection-deposits', stayWindow, propertyId, state],
    queryFn: ({ signal }) =>
      api.getProtectionDeposits(
        { window: stayWindow, propertyId: propertyId || undefined, state: state || undefined },
        signal
      ),
    staleTime: 30_000,
    // Holds move on the hourly sweep; a page left open should catch up.
    refetchInterval: 5 * 60_000,
  })

  // Search narrows what is already on screen; it decides nothing about state.
  const deposits = useMemo(() => {
    const rows = data?.deposits ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter(d =>
      [d.guest_name, d.property_name, d.reservation_id].some(v =>
        String(v ?? '')
          .toLowerCase()
          .includes(needle)
      )
    )
  }, [data, search])

  const hasFilters = Boolean(propertyId || state || search.trim())

  // Open a linked booking once, when the list it belongs to has loaded.
  // Adjusted during render (React's pattern for state derived from data),
  // not in an effect, so there is no extra render with the drawer closed.
  if (pendingBooking && data) {
    const match = data.deposits.find(d => d.reservation_id === pendingBooking)
    setPendingBooking(null)
    if (match) setSelected(match)
  }

  // The drawer follows the latest data, so a refetch while it is open (the
  // five-minute poll, or a settings change) shows the current state rather
  // than the snapshot taken at click time. It keeps the snapshot only if the
  // row has since left the list.
  const selectedDeposit = selected
    ? (data?.deposits.find(d => d.reservation_id === selected.reservation_id) ?? selected)
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Bookings
          {data && (
            <span className="rounded-full bg-surface2 px-2 py-0.5 text-xs font-semibold text-textMuted">
              {deposits.length}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Every booking and what its security deposit is doing. Airbnb never shares guest cards, so
          its bookings are not listed.
          {data && !data.automation_enabled && (
            <>
              {' '}
              <span className="text-amber">
                Automation is off: rows marked Simulated show what would have happened, and no card
                was charged.
              </span>
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex min-w-[200px] flex-1 flex-col gap-1 sm:max-w-xs">
            <span className="text-xs font-medium text-textMuted">Search</span>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted"
                aria-hidden
              />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Guest or property"
                className="h-9 pl-8"
              />
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Show</span>
            <select
              value={stayWindow}
              onChange={e => setStayWindow(e.target.value as Window)}
              className={selectClass}
            >
              <option value="upcoming">Upcoming</option>
              <option value="past">Past 3 months</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Property</span>
            <select
              value={propertyId}
              onChange={e => setPropertyId(e.target.value)}
              className={selectClass}
            >
              <option value="">All properties</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>
                  {getPropertyDisplayName(p as never, p.id ?? '')}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-textMuted">Status</span>
            <select value={state} onChange={e => setState(e.target.value)} className={selectClass}>
              <option value="">All statuses</option>
              {PROTECTION_DEPOSIT_STATES.map(s => (
                <option key={s} value={s}>
                  {statePresentation(s).label}
                </option>
              ))}
            </select>
          </label>

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="text-textMuted"
              onClick={() => {
                setPropertyId('')
                setState('')
                setSearch('')
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {isLoading ? (
          <LoadingSkeleton className="h-40 w-full" />
        ) : error && !data ? (
          <ErrorState error={error as Error} onRetry={refetch} />
        ) : deposits.length === 0 ? (
          <EmptyState
            message={
              hasFilters
                ? 'No bookings match these filters.'
                : stayWindow === 'upcoming'
                  ? 'No upcoming VRBO or direct bookings in the next 90 days.'
                  : 'No VRBO or direct checkouts in the last 90 days.'
            }
          />
        ) : (
          <>
            {error && (
              <p className="mb-3 text-sm text-warning">
                Couldn&apos;t refresh just now, so this may be out of date.{' '}
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="underline underline-offset-2"
                >
                  Try again
                </button>
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Deposit</TableHead>
                  <TableHead>Stay</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deposits.map(deposit => {
                  const presentation = statePresentation(deposit.state)
                  return (
                    <TableRow
                      key={deposit.reservation_id}
                      className="cursor-pointer"
                      onClick={() => setSelected(deposit)}
                    >
                      <TableCell>
                        {/* The button is what makes the row reachable by
                            keyboard; the row's own click is a convenience. */}
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation()
                            setSelected(deposit)
                          }}
                          className="text-left font-medium text-text hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
                        >
                          {deposit.guest_name || `Booking ${deposit.reservation_id}`}
                        </button>
                        <div className="text-xs text-textMuted">{channelName(deposit.channel)}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {deposit.property_name || deposit.property_id}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant={presentation.variant}
                            title={presentation.hint}
                            className="whitespace-nowrap"
                          >
                            {presentation.label}
                          </Badge>
                          {deposit.mode === 'dry_run' && (
                            <Badge variant="secondary">Simulated</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{describeDeposit(deposit)}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatStay(deposit.check_in, deposit.check_out)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>

      <DepositDrawer deposit={selectedDeposit} onClose={() => setSelected(null)} />
    </Card>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <dt className="text-textMuted">{label}</dt>
      <dd className="text-right text-text">{children}</dd>
    </div>
  )
}

/** One booking's deposit, and its hold timeline, newest first. */
function DepositDrawer({
  deposit,
  onClose,
}: {
  deposit: ProtectionDeposit | null
  onClose: () => void
}) {
  const presentation = deposit ? statePresentation(deposit.state) : null
  return (
    <Dialog open={deposit !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {deposit && presentation && (
          <>
            <DialogHeader>
              <DialogTitle>{deposit.guest_name || `Booking ${deposit.reservation_id}`}</DialogTitle>
              <DialogDescription>
                {deposit.property_name || deposit.property_id} ·{' '}
                {formatStay(deposit.check_in, deposit.check_out)}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={presentation.variant}>{presentation.label}</Badge>
              {deposit.mode === 'dry_run' && <Badge variant="secondary">Simulated</Badge>}
            </div>
            <p className="text-sm text-textMuted">{stateHint(deposit)}</p>

            <dl className="divide-y divide-border">
              <DetailRow label="Deposit">{formatDepositAmount(deposit.amount_cents)}</DetailRow>
              {deposit.captured_amount_cents != null && (
                <DetailRow label="Captured">
                  {formatDepositAmount(deposit.captured_amount_cents)}
                </DetailRow>
              )}
              {deposit.refunded_amount_cents != null && (
                <DetailRow label="Refunded">
                  {formatDepositAmount(deposit.refunded_amount_cents)}
                </DetailRow>
              )}
              {deposit.dispute_status && (
                <DetailRow label="Dispute">{disputeLabel(deposit.dispute_status)}</DetailRow>
              )}
              {/* Not for a simulated hold: its expiry is an estimate for a hold
                  that does not exist. */}
              {deposit.hold_until && (
                <DetailRow label="Kept through">
                  {formatLocaleDateTime(deposit.hold_until)}
                  {deposit.hold_until_reservation_id
                    ? ` (booking ${deposit.hold_until_reservation_id})`
                    : ''}
                </DetailRow>
              )}
              {deposit.state === 'held' &&
                deposit.mode !== 'dry_run' &&
                deposit.authorization_expires_at && (
                  <DetailRow label="Hold good until">
                    {formatLocaleDateTime(deposit.authorization_expires_at)}
                  </DetailRow>
                )}
              <DetailRow label="Channel">{channelName(deposit.channel)}</DetailRow>
              {deposit.last_error && <DetailRow label="Last error">{deposit.last_error}</DetailRow>}
            </dl>

            <div className="flex flex-wrap gap-3 text-sm">
              <Link
                to={`/reservations/${encodeURIComponent(deposit.reservation_id)}`}
                className="text-mint hover:underline"
              >
                Open booking
              </Link>
              {deposit.stripe_url && (
                <a
                  href={deposit.stripe_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-mint hover:underline"
                >
                  Open in Stripe
                  <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-text">Timeline</h3>
              {deposit.history.length === 0 ? (
                <p className="text-sm text-textMuted">Nothing has happened yet.</p>
              ) : (
                <ol className="space-y-2">
                  {deposit.history.map((entry, index) => (
                    <li key={`${entry.at}-${index}`} className="text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium text-text">{historyLabel(entry.action)}</span>
                        <span className="whitespace-nowrap text-xs text-textMuted">
                          {formatLocaleDateTime(entry.at)}
                        </span>
                      </div>
                      {historyDetail(entry) && (
                        <div className="wrap-break-word text-xs text-textMuted">
                          {historyDetail(entry)}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
